import React, { useEffect, useCallback, useState } from 'react';
import { Tabs, Button, Popconfirm, message, Spin } from 'antd';
import {
  VerticalAlignTopOutlined,
  LeftOutlined,
  RightOutlined,
  VerticalAlignBottomOutlined,
  PlusOutlined,
  EditOutlined,
  SaveOutlined,
  RollbackOutlined,
  DeleteOutlined,
  SearchOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { usePWH203Mode } from './hooks/usePWH203Mode';
import { usePWH203Record } from './hooks/usePWH203Record';
import { usePWH203Navigation } from './hooks/usePWH203Navigation';
import Tab1Main from './components/Tab1Main';
import Tab2Details from './components/Tab2Details';
import Tab3Expenses from './components/Tab3Expenses';
import './PWH203Form.css';

const PWH203Form: React.FC = () => {
  const navigate = useNavigate();
  const { mode, isEditing, setAdd, setEdit, setBrowse } = usePWH203Mode();
  const [activeTab, setActiveTab] = useState('1');
  const [searchId, setSearchId] = useState('');

  const {
    record, formData, details, expenses, loading,
    loadRecord, resetForAdd, revertToRecord,
    getField, setField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow, updateDetailRow,
  } = usePWH203Record();

  const loadWithRecord = useCallback(
    async (id: string) => { await loadRecord(id); },
    [loadRecord]
  );

  const { allIds, currentIdx, loadList, navTo } = usePWH203Navigation(loadWithRecord);

  useEffect(() => { loadList(); }, [loadList]);

  const isApproved = record?.fh130_80 === 'P';

  // ─── Action Handlers ────────────────────────────────────────

  const handleAddNew = () => {
    resetForAdd();
    setAdd();
    setActiveTab('1');
  };

  const handleEditClick = () => {
    if (!record) { message.warning('請先選擇一筆資料'); return; }
    if (isApproved) { message.warning('已核准記錄不可修改'); return; }
    setEdit();
  };

  const handleSaveClick = () => { handleSave(setBrowse); };

  const handleRevert = () => {
    revertToRecord();
    setBrowse();
  };

  const handleDeleteClick = () => {
    if (isApproved) { message.warning('已核准記錄不可刪除'); return; }
    handleDelete(setBrowse);
  };

  const handleSearch = async () => {
    if (!searchId.trim()) return;
    try {
      await loadRecord(searchId.trim());
      const idx = allIds.indexOf(searchId.trim());
      if (idx >= 0) navTo(idx);
    } catch {
      message.error('查無此沖帳單號');
    }
  };

  // ─── Tabs ───────────────────────────────────────────────────

  const tabItems = [
    {
      key: '1',
      label: '主表資料',
      children: (
        <Tab1Main
          formData={formData}
          isEditing={isEditing}
          getField={getField}
          setField={setField}
        />
      ),
    },
    {
      key: '2',
      label: `沖帳明細 (${details.length})`,
      children: (
        <Tab2Details
          details={details}
          isEditing={isEditing}
          onAdd={addDetailRow}
          onDelete={deleteDetailRow}
          onUpdate={updateDetailRow}
        />
      ),
    },
    {
      key: '3',
      label: `費用分攤 (${expenses.length})`,
      children: (
        <Tab3Expenses
          expenses={expenses}
          isEditing={isEditing}
        />
      ),
    },
  ];

  // ─── Render ─────────────────────────────────────────────────

  return (
    <div className="pwh203-form">
      <div className="pwh203-page-wrapper">
        <Spin spinning={loading}>
          <div className="pwh203-header">
            <h2>薪資/費用沖帳單</h2>
            {record && (
              <span style={{ color: '#888', fontSize: 13 }}>
                單號：{record.fh130_01}
                {isApproved && (
                  <span style={{ color: '#ff4d4f', marginLeft: 8, fontWeight: 'bold' }}>
                    [已核准]
                  </span>
                )}
              </span>
            )}
          </div>

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            size="small"
          />
        </Spin>
      </div>

      {/* ActionBar */}
      <div className="pwh203-action-bar">
        {/* Navigation */}
        <Button
          icon={<VerticalAlignTopOutlined />}
          size="small"
          disabled={isEditing || currentIdx <= 0}
          onClick={() => navTo(0)}
          title="最前"
        />
        <Button
          icon={<LeftOutlined />}
          size="small"
          disabled={isEditing || currentIdx <= 0}
          onClick={() => navTo(currentIdx - 1)}
          title="上一筆"
        />
        <Button
          icon={<RightOutlined />}
          size="small"
          disabled={isEditing || currentIdx >= allIds.length - 1}
          onClick={() => navTo(currentIdx + 1)}
          title="下一筆"
        />
        <Button
          icon={<VerticalAlignBottomOutlined />}
          size="small"
          disabled={isEditing || currentIdx >= allIds.length - 1}
          onClick={() => navTo(allIds.length - 1)}
          title="最後"
        />

        <span className="divider" style={{ borderLeft: '1px solid #e8e8e8', height: 20, margin: '0 4px' }} />

        {/* Search */}
        <input
          style={{ width: 100, padding: '2px 6px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 12 }}
          placeholder="沖帳單號"
          value={searchId}
          onChange={(e) => setSearchId(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          disabled={isEditing}
        />
        <Button
          icon={<SearchOutlined />}
          size="small"
          disabled={isEditing}
          onClick={handleSearch}
          title="查詢"
        />

        <span className="divider" style={{ borderLeft: '1px solid #e8e8e8', height: 20, margin: '0 4px' }} />

        {/* Add / Save */}
        {!isEditing ? (
          <Button icon={<PlusOutlined />} size="small" onClick={handleAddNew} type="primary">
            新增
          </Button>
        ) : (
          <Button icon={<SaveOutlined />} size="small" onClick={handleSaveClick} type="primary">
            存檔
          </Button>
        )}

        {/* Edit / Revert */}
        {!isEditing ? (
          <Button icon={<EditOutlined />} size="small" onClick={handleEditClick} disabled={!record}>
            修改
          </Button>
        ) : (
          <Button icon={<RollbackOutlined />} size="small" onClick={handleRevert}>
            還原
          </Button>
        )}

        {/* Delete */}
        <Popconfirm
          title="確定要刪除此筆資料?"
          onConfirm={handleDeleteClick}
          okText="確定"
          cancelText="取消"
          disabled={isEditing || !record}
        >
          <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            disabled={isEditing || !record}
          >
            刪除
          </Button>
        </Popconfirm>

        <span className="divider" style={{ borderLeft: '1px solid #e8e8e8', height: 20, margin: '0 4px' }} />

        {/* Leave */}
        <Button icon={<LogoutOutlined />} size="small" onClick={() => navigate('/erp')}>
          離開
        </Button>

        {allIds.length > 0 && (
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#888' }}>
            {currentIdx + 1} / {allIds.length}
          </span>
        )}
      </div>
    </div>
  );
};

export default PWH203Form;
